import { reorderArrayItems } from '@deltek/specification-client/core/Utilities/formUtils'
import { ProductListingDetailConsolidatedProductMedia as ConsolidatedProductMedia } from '@deltek/specification-client/core/Widgets/ProductListing/Details/Media/productListingDetailConsolidatedProductMedia'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import React from 'react'
import {
    DragDropContext,
    Draggable,
    Droppable,
    DropResult,
} from 'react-beautiful-dnd'
import { GalleryItem } from './GalleryItem'
import TabResultArea from './TabResultArea'

const galleryTabContentStyles = makeStyles({
    draggableContainer: {
        backgroundColor: '#fff',
    },
})

export interface GalleryTabContentProps {
    mediaDialogConsolidatedProductMedia: Record<
        string,
        ConsolidatedProductMedia
    >
    mediaDialogConsolidatedProductMediaKeys: string[]
    setMediaDialogConsolidatedProductMedia: (
        updatedObj: Record<string, ConsolidatedProductMedia>
    ) => void
}

const GalleryTabContent = ({
    mediaDialogConsolidatedProductMedia,
    mediaDialogConsolidatedProductMediaKeys,
    setMediaDialogConsolidatedProductMedia,
}: GalleryTabContentProps) => {
    const classes = galleryTabContentStyles()
    const orderedItemKeys = mediaDialogConsolidatedProductMediaKeys.sort(
        (key1, key2) =>
            mediaDialogConsolidatedProductMedia[key1].order >
            mediaDialogConsolidatedProductMedia[key2].order
                ? 1
                : -1
    )

    const handleDragEnd = ({ destination, source }: DropResult) => {
        if (!destination) return

        const reorderedItemKeys = reorderArrayItems(
            orderedItemKeys,
            source.index,
            destination.index
        )

        const updatedObj: Record<string, ConsolidatedProductMedia> = {}
        reorderedItemKeys.forEach((key, idx) => {
            const currentMedia = mediaDialogConsolidatedProductMedia[key]
            updatedObj[key] = {
                ...currentMedia,
                order: idx,
            }
        })

        setMediaDialogConsolidatedProductMedia(updatedObj)
    }

    const handleOnRemoveClick = (mediaId: string) => {
        const shallowMediaCopy = { ...mediaDialogConsolidatedProductMedia }
        delete shallowMediaCopy[mediaId]

        // reorder items
        const newKeys = mediaDialogConsolidatedProductMediaKeys.filter(
            (each) => each !== mediaId
        )

        const orderedObj: Record<string, ConsolidatedProductMedia> = {}
        newKeys.forEach((key, idx) => {
            const currentMedia = shallowMediaCopy[key]
            orderedObj[key] = {
                ...currentMedia,
                order: idx,
            }
        })

        setMediaDialogConsolidatedProductMedia(orderedObj)
    }

    return (
        <TabResultArea
            title={`${mediaDialogConsolidatedProductMediaKeys.length} of 10 photos/videos added`}
        >
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="galleryItemDroppable">
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                        >
                            {orderedItemKeys.map((key, idx) => (
                                <Draggable
                                    key={idx}
                                    draggableId={idx.toString()}
                                    index={idx}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className={clsx(
                                                snapshot.isDragging &&
                                                    'isDragging',
                                                classes.draggableContainer
                                            )}
                                        >
                                            <GalleryItem
                                                index={idx + 1}
                                                mediaId={key}
                                                onRemoveClick={
                                                    handleOnRemoveClick
                                                }
                                                title={
                                                    mediaDialogConsolidatedProductMedia[
                                                        key
                                                    ].originalFilename ?? ''
                                                }
                                                thumbnailFetching={
                                                    mediaDialogConsolidatedProductMedia[
                                                        key
                                                    ].thumbnailUrlFetching
                                                }
                                                imageUrl={
                                                    mediaDialogConsolidatedProductMedia[
                                                        key
                                                    ].thumbnailUrl
                                                }
                                                isVideo={
                                                    !!mediaDialogConsolidatedProductMedia[
                                                        key
                                                    ].videoId
                                                }
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </TabResultArea>
    )
}

export default GalleryTabContent
